// @LTG-ID: data/journals/family-succession-vs-selling
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 27 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Exit Planning (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "family-succession-vs-selling",
    FRONTMATTER: {
        number: "64",
        section: "Exit Planning",
        sectionHref: "/journals#exit-planning",
        sectionAnchor: "exit-planning",
        title: "Family Succession vs Selling to a Third Party: Choosing Your Exit Path",
        deck: "Family succession preserves legacy and continuity but usually delivers less liquidity, more slowly, with higher execution risk; research shows only about 30 percent of family businesses survive into the second generation.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-10",
        publishedHuman: "June 10, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 519,
        timeRequired: "PT3M",
        keywords: "family succession vs selling business, passing business to children, sell business or keep in family, business succession options",
        articleSection: "Exit Planning",
        articleBodyPreview: "Family succession preserves legacy and continuity but usually delivers less liquidity, more slowly, with higher execution risk; research shows only about 30 percent of family businesses survive into the second generation. A third party sale maximizes and de risks proceeds but ends family ownership. The right path depends on goals, successors, and finances. \"The kids will take over someday\" is one of the most common exit plans in America, and one of the least examined. It deserves the same rigor ",
        complianceNote: "This article is educational and does not constitute legal, tax, or financial advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "exit-planning-guide",
        "personal-readiness-to-exit",
        "walk-away-number-selling-business",
        "sell-business-to-employees"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Family Succession vs Third-Party Sale: Your Exit Path · LTG",
        canonical: "https://letstalkgrowth.com/journals/family-succession-vs-selling",
        ogImage: "/journals/og/family-succession-vs-selling.jpg",
    },
    BODY_MARKDOWN: `Family succession preserves legacy and continuity but usually delivers less liquidity, more slowly, with higher execution risk; research shows only about 30 percent of family businesses survive into the second generation. A third-party sale maximizes and de-risks proceeds but ends family ownership. The right path depends on goals, successors, and finances.

"The kids will take over someday" is one of the most common exit plans in America, and one of the least examined. It deserves the same rigor as any other transaction, because it is one, with family relationships added to the stakes.

## The honest statistics

Research on family business continuity, widely cited from the Family Business Institute and academic studies of multigenerational firms, has long reported sobering numbers: only roughly three in ten family businesses survive the transition into the second generation, and far fewer make it to the third. The failures rarely come from bad intentions. They come from unprepared successors, undiscussed expectations, financial structures that starve either the parents' retirement or the business's working capital, and the corrosive assumption that proximity equals readiness. Verify the current figures against the sources before quoting them, but the pattern has held for decades.

## What each path really offers

**Family succession** offers legacy, continuity for employees and customers, a role for the founder that can taper rather than end, and the deep satisfaction of a business that outlives its builder. Its costs are equally real: proceeds usually arrive slowly, funded from the business's own cash flow through installment purchases, gifting strategies, or hybrids your CPA and attorney must design; the founder's retirement security becomes entangled with the successor's performance; and the plan requires something many families never verify, a successor who genuinely wants the business and can genuinely run it. Wanting to inherit and wanting to operate are different desires, and confusing them is the classic failure.

**A third-party sale** offers maximum proceeds, mostly at closing, professional distance, and a clean separation of the family's wealth from the business's future risk. Its costs: the legacy passes to strangers, changes follow, and the family role ends, which lands harder emotionally than most founders predict.

There are also blended paths worth knowing: selling to management or employees, covered in the next article, or a partial sale that brings in capital and a partner while family retains a stake.

## The questions that decide it

Four questions, answered honestly, resolve most of these decisions. Does a willing and able successor actually exist, tested by real responsibility, not assumed by surname? Does the founder's financial plan work if the business pays for the exit slowly, per the walk-away math in this series? Can the family govern the arrangement, with the non-participating children's inheritance expectations addressed explicitly rather than avoided? And is everyone choosing this path, or merely defaulting into it because the conversation about alternatives felt disloyal?

Families that answer all four well beat the survival statistics. Families that skip them become the statistics. Either way, the path chosen should be a decision with a runway, several years for a family transition done properly, not a default discovered at retirement.`,
    FAQ: [
        { question: "Do family successions really fail that often?", answer: "Long-cited research puts second-generation survival around 30 percent, with third-generation survival far lower. The dominant causes are successor readiness, family communication, and financial structure, all addressable with early, deliberate planning." },
        { question: "How do parents get paid in a family succession?", answer: "Common structures include installment sales funded by business cash flow, gifting strategies combined with partial sales, and hybrids designed around tax efficiency. Each entangles the parents' retirement with the business's performance, which is why the founder's independent financial plan must be stress-tested first." },
        { question: "Should children pay full market price for the family business?", answer: "Families choose points along a spectrum from full-price sale to substantial gift, balancing the parents' financial needs, tax planning, and fairness among siblings. What matters most is that the choice is explicit, professionally structured, and understood by everyone affected." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Exit Planning",
            title: "Exit Planning 101: How to Leave Your Business on Your Own Terms",
            meta: "4 min · Brief 39",
            to: "/journals/exit-planning-guide",
        },
        {
            tag: "Personal Readiness",
            title: "Are You Personally Ready to Exit? The Owner's Guide to Financial and Life Readiness",
            meta: "4 min · Brief 58",
            to: "/journals/personal-readiness-to-exit",
        },
        {
            tag: "Personal Readiness",
            title: "What Is Your Walk-Away Number? Calculating How Much You Need From the Sale",
            meta: "3 min · Brief 59",
            to: "/journals/walk-away-number-selling-business",
        },
    ],
};

export default BRIEF;
